import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  getOrgConfigPublic,
  getSurvey,
  listOrg,
  putSubmissionPortal,
} from "@rubi/api-generated";
import { useNavigate, useParams } from "react-router-dom";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";

import { defaultTheme } from "./constants";
import "survey-core/survey-core.min.css";
import "./ViewReport.css";
import { registerAutocomplete } from "./custom-questions/AddressAutocompleteQuestion/AddressAutocompleteQuestion";
import { registerDriversLicenseScanner } from "./custom-questions/DriversLicenseScannerQuestion";
import { registerNarrativeCanvas } from "./custom-questions/NarrativeCanvasQuestion/NarrativeCanvasQuestion";
import { createSurveyHandlers } from "./survey-handlers";

const CISOSubmissionCreate = () => {
  const navigate = useNavigate();
  const appName = window.location.pathname.split("/")[1];
  const [surveyModel, setSurveyModel] = useState<Model>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [attachmentIDs, setAttachmentIDs] = useState<{ [key: string]: string }>(
    {},
  );
  const [newSubmissionID, setNewSubmissionID] = useState<string>("");
  const saveDraftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const { surveyClass, orgId } = useParams();
  const { currentUser, currentUserAgency } = useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();

  const CISO_ORG_ID = isDevEnv
    ? "4b556c8c-e5fd-4e1d-93fc-81d2e5f5bea4"
    : "72568a9a-3d58-4c13-9cb2-604118ce7303";

  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );

  const handlers = createSurveyHandlers({
    id: newSubmissionID,
    appName,
    attachmentIDs,
    setAttachmentIDs,
    organizationConfig: {
      generateAttachmentDescriptions: true,
    },
    navigate,
  });

  useEffect(() => {
    const createNewSubmission = async () => {
      try {
        setIsLoading(true);

        // Get the survey_id from CISO config based on survey_class
        const cisoConfig = await getOrgConfigPublic({
          path: { org_id: CISO_ORG_ID },
        });

        const survey = cisoConfig.data?.surveys.find(
          (s) => s.survey_class === surveyClass && !s.is_registry,
        );

        if (!survey) {
          setError("Survey not found");
          return;
        }

        // Create a new submission
        const response = await putSubmissionPortal({
          path: { org_id: CISO_ORG_ID },
          body: {
            survey_id: survey.survey_id,
          },
        });

        setNewSubmissionID(response.data!.submission_id);

        // Fetch the survey configuration
        const surveyData = await getSurvey({
          path: { survey_id: survey.survey_id },
        });

        // Create survey model
        const model = new Model(surveyData.data!.survey.configuration);
        model.applyTheme(defaultTheme);

        const orgsResponse = await listOrg({ throwOnError: false });
        const org = orgsResponse.data?.find(
          (o) => o.id === currentUser?.rubi_org_id,
        );

        // Prefill form fields with logged-in user's details
        if (currentUser) {
          const prefilledData: { [key: string]: string } = {};

          if (org?.title) {
            prefilledData["person-reportingParty_agency"] = org.title;
          }
          if (currentUser.first_name) {
            prefilledData["person-reportingParty_firstName"] =
              currentUser.first_name;
          }
          if (currentUser.last_name) {
            prefilledData["person-reportingParty_lastName"] =
              currentUser.last_name;
          }
          if (currentUser.contact_phone_number) {
            prefilledData["person-reportingParty_phoneNumber"] =
              currentUser.contact_phone_number;
          }
          if (currentUser.email) {
            prefilledData["person-reportingParty_email"] = currentUser.email;
          }
          if (currentUser.rank_short) {
            prefilledData["person-reportingParty_rank"] =
              currentUser.rank_short;
          }
          if (currentUser.unit) {
            prefilledData["person-reportingParty_unit"] = currentUser.unit;
          }

          Object.keys(prefilledData).forEach((key) => {
            model.setValue(key, prefilledData[key]);
          });
        }

        setSurveyModel(model);
      } catch (error: unknown) {
        console.error("Error creating submission:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Failed to create submission",
        );
      } finally {
        setIsLoading(false);
      }
    };

    registerAutocomplete();
    registerDriversLicenseScanner();
    registerNarrativeCanvas();
    createNewSubmission();
  }, [surveyClass, orgId, currentUser, currentUserAgency]);

  const performSaveDraft = useCallback(async () => {
    if (!surveyModel || !newSubmissionID || !handlers.saveDraft) return;
    try {
      await handlers.saveDraft(surveyModel);
    } catch (err) {
      console.error("Error saving draft:", err);
    } finally {
      setIsSavingDraft(false);
    }
  }, [surveyModel, newSubmissionID, handlers]);

  const saveDraft = useCallback(() => {
    if (!newSubmissionID || isSavingDraft) return;
    if (saveDraftTimeoutRef.current) {
      clearTimeout(saveDraftTimeoutRef.current);
    }
    const timeout = setTimeout(() => {
      setIsSavingDraft(true);
      performSaveDraft();
    }, 500);
    saveDraftTimeoutRef.current = timeout;
  }, [newSubmissionID, isSavingDraft, performSaveDraft]);

  // Set up survey event handlers
  useEffect(() => {
    if (!surveyModel || !newSubmissionID) return;

    surveyModel.onComplete.clear();

    surveyModel.onComplete.add(async (sender, options) => {
      const success = await handlers.submitForm(sender, options);
      if (success) {
        // Cosmetic delay only
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 2000));
        navigate(`/rubiintel/${orgId}/report/${newSubmissionID}/complete`);
      }
    });

    surveyModel.onUploadFiles.clear();
    surveyModel.onUploadFiles.add((survey, options) =>
      handlers.uploadFiles(survey, options),
    );

    surveyModel.onClearFiles.clear();
    surveyModel.onClearFiles.add((_, options) => handlers.clearFiles(options));

    surveyModel.onCurrentPageChanged.clear();
    surveyModel.onCurrentPageChanged.add(() => {
      saveDraft();
      window.scrollTo(0, 0);
    });
  }, [surveyModel, newSubmissionID, handlers, saveDraft]);

  useEffect(() => {
    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return <div style={{ padding: 20, textAlign: "center" }}>Loading...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!surveyModel) {
    return null;
  }

  return <Survey model={surveyModel} />;
};

export default CISOSubmissionCreate;
